import { z } from "zod"
import { JuloUtils } from "../common/JuloUtils"
import { TranslationKey, Translator } from "../i18n/translation-key"
import { PartGrade } from "./part-grade"
import { PartCategory } from "./service-bike-part"
import { ServiceAction } from "./service-part-action"

export enum ServiceBikeBrakeSystem {
  HYDRAULIC_DISC = "HYDRAULIC_DISC", // Hydraulisk skivbroms
  MECHANIC_DISC = "MECHANIC_DISC", // Mekanisk skivbroms
  HYDRAULIC_RIM = "HYDRAULIC_RIM", // Hydraulisk fälgbroms
  MECHANIC_RIM = "MECHANIC_RIM", // Mekanisk fälgbroms
  ROLLER = "ROLLER", // Rullbroms
  COASTER = "COASTER", // Fotbroms (bara aktuellt för bakbroms)
  NONE = "NONE", // Inget
}

export interface ServiceBikeCondition {
  mechanicName: string
  parts: ServiceBikePartAfterService[]
  isCompleted: boolean
}

export interface ServiceBikePartAfterService {
  partId: string
  partName: string
  partImage: string
  category: PartCategory
  recommendedAction: ServiceAction
  /** Custom label for the recommended action (e.g., for damper service custom names) */
  recommendedActionLabel?: string
  actionThatWillBePerformed: ServiceAction
  /** Custom label for the action that will be performed (e.g., for damper service custom names) */
  actionThatWillBePerformedLabel?: string
  oldGrade: PartGrade | null
  newGrade: PartGrade | null
  commentFromMechanic: string | null
  /**
   * Why the component needs attention although its lifetime says otherwise:
   * set when the mechanic marked it as not working (optimally).
   */
  functionNotice: string | null
}

export enum ServiceBikeGearSystem {
  HUB = "HUB", // Navväxel
  EXTERNAL = "EXTERNAL", // Utanpåliggande växlar (derailleur)
  SINGLE_SPEED = "SINGLE_SPEED", // Enväxlad
}

export enum ServiceBikeType {
  ELECTRIC_SCOOTER = "ELECTRIC_SCOOTER", // Elsparkcykel
  MOPED = "MOPED", // Moped
  CITY = "CITY", // Stadscykel
  HYBRID = "HYBRID", // Hybridcykel
  MOUNTAIN = "MOUNTAIN", // Mountainbike
  GRAVEL = "GRAVEL", // Gravelbike
  COUNTRY_ROAD = "COUNTRY_ROAD", // Landsvägscykel
  CARGO = "CARGO", // Lådcykel
  BALANCE_BIKE = "BALANCE_BIKE", // Balanscykel
  CHILDREN_BIKE = "CHILDREN_BIKE", // Barncykel
  JUNIOR_BIKE = "JUNIOR_BIKE", // Juniorcykel
  CYCLOCROSS = "CYCLOCROSS", // Cyclocross
  VINTAGE = "VINTAGE", // Vintage
  FOLDABLE = "FOLDABLE", // Hopfällbar
  CRUISER = "CRUISER", // Cruiser
  BMX = "BMX", // BMX
  FAT_BIKE = "FAT_BIKE", // Fatbike
  TRIKE = "TRIKE", // Trehjuling
  FIXED_GEAR = "FIXED_GEAR", // Fixed gear
  LOOSE_PART = "LOOSE_PART", // Lös del
  OTHER = "OTHER", // Annan
  ALPINE_SKI = "ALPINE_SKI", // Alpinskidor
  CROSS_COUNTRY_SKI = "CROSS_COUNTRY_SKI", // Längdskidor
  SNOWBOARD = "SNOWBOARD", // Snowboard
  TOURING_SKATES = "TOURING_SKATES", // Långfärdsskridskor
  ICE_SKATES = "ICE_SKATES", // Skridskor
}

/** Actual bicycle types (excludes scooters, winter sports, loose parts) */
export const BICYCLE_TYPES: ServiceBikeType[] = [
  ServiceBikeType.CITY,
  ServiceBikeType.HYBRID,
  ServiceBikeType.MOUNTAIN,
  ServiceBikeType.GRAVEL,
  ServiceBikeType.COUNTRY_ROAD,
  ServiceBikeType.CARGO,
  ServiceBikeType.BALANCE_BIKE,
  ServiceBikeType.CHILDREN_BIKE,
  ServiceBikeType.JUNIOR_BIKE,
  ServiceBikeType.CYCLOCROSS,
  ServiceBikeType.VINTAGE,
  ServiceBikeType.FOLDABLE,
  ServiceBikeType.CRUISER,
  ServiceBikeType.BMX,
  ServiceBikeType.FAT_BIKE,
  ServiceBikeType.TRIKE,
  ServiceBikeType.FIXED_GEAR,
]

/** Winter sports equipment types */
export const WINTER_SPORTS_TYPES: ServiceBikeType[] = [
  ServiceBikeType.ALPINE_SKI,
  ServiceBikeType.CROSS_COUNTRY_SKI,
  ServiceBikeType.SNOWBOARD,
  ServiceBikeType.TOURING_SKATES,
  ServiceBikeType.ICE_SKATES,
]

/** Non-bicycle service types (scooters, mopeds, winter sports equipment) */
export const NON_BICYCLE_TYPES: ServiceBikeType[] = [
  ServiceBikeType.ELECTRIC_SCOOTER,
  ServiceBikeType.MOPED,
  ...WINTER_SPORTS_TYPES,
]

/** All serviceable equipment types (bicycles + non-bicycles) */
export const ALL_SERVICE_TYPES: ServiceBikeType[] = [
  ...BICYCLE_TYPES,
  ...NON_BICYCLE_TYPES,
]

export function isNonBicycleType(type: ServiceBikeType): boolean {
  return NON_BICYCLE_TYPES.includes(type)
}

/**
 * Normalizes isElectric value based on bike type.
 * - Electric scooters are always electric (returns true)
 * - Winter sports (skis, skates, snowboards) return false since the concept doesn't apply
 * - Bicycles return the provided value or false if null/undefined
 */
export function normalizeIsElectric(
  type: ServiceBikeType,
  isElectric: boolean | null | undefined,
): boolean {
  if (type === ServiceBikeType.ELECTRIC_SCOOTER) {
    return true
  }
  if (WINTER_SPORTS_TYPES.includes(type)) {
    return false
  }
  return isElectric ?? false
}

export interface ElectricSystem {
  id: string
  name: string
  logo?: string | null
}

export const SaveElectricSystemSchema = z.object({
  name: z.string().min(1),
})

export type SaveElectricSystem = z.infer<typeof SaveElectricSystemSchema>

export const BikeBrandSchema = z.object({
  id: z.guid(),
  name: z.string(),
  alias: z.array(z.string()),
  logo: z.string().nullable(),
})

export type BikeBrand = z.infer<typeof BikeBrandSchema>

export const SaveBikeBrandSchema = BikeBrandSchema.pick({
  name: true,
})

export type SaveBikeBrand = z.infer<typeof SaveBikeBrandSchema>

/** An axle has one wheel, or two side by side (cargo bikes and trikes) */
export const WheelCountSchema = z.number().int().min(1).max(2)

export const ServiceBikeSchema = z.object({
  id: z.guid(),
  brand: z
    .object({
      id: z.guid(),
      name: z.string(),
    })
    .nullable(),
  model: z.string().nullable(),
  bikeBrandId: z.guid().nullable(),
  color: z.string().nullable(),
  type: z.nativeEnum(ServiceBikeType),
  customBikeTypeId: z.guid().nullable(),
  customBikeTypeName: z.string().nullable(),
  brakeSystemFront: z.nativeEnum(ServiceBikeBrakeSystem).nullable(),
  brakeSystemBack: z.nativeEnum(ServiceBikeBrakeSystem).nullable(),
  gearSystem: z.nativeEnum(ServiceBikeGearSystem).nullable(),
  bikeHasFrontGears: z.boolean(),
  bikeHasDamperFront: z.boolean(),
  bikeHasDamperBack: z.boolean(),
  hasElectricFrontGears: z.boolean(),
  hasElectricRearGears: z.boolean(),
  /** Wheels on the front axle as stored; read through {@link resolveWheelCounts} */
  frontWheelCount: WheelCountSchema.nullable(),
  /** Wheels on the rear axle as stored; read through {@link resolveWheelCounts} */
  rearWheelCount: WheelCountSchema.nullable(),
  isElectric: z.boolean(),
  publishedToReRideAt: z.date().nullable(),
  shopifyProductId: z.string().nullable(),
})

/**
 * Clients built before the wheel counts existed don't send them; a cargo bike
 * then keeps the counts it has.
 */
const OptionalWheelCountsSchema = z.object({
  frontWheelCount: WheelCountSchema.nullable().optional(),
  rearWheelCount: WheelCountSchema.nullable().optional(),
})

export const SaveServiceBikeSchema = ServiceBikeSchema.omit({
  id: true,
  brand: true,
})
  .extend({ id: z.string().optional() })
  .merge(OptionalWheelCountsSchema)

export const UpdateServiceBikeSchema = ServiceBikeSchema.omit({
  id: true,
  brand: true,
  publishedToReRideAt: true,
  shopifyProductId: true,
})
  .merge(OptionalWheelCountsSchema)
  .extend({
    /** The bike picked from the customer's bikes; moves the job onto it. */
    previousBikeId: z.guid().optional(),
    /** Takes the job off the bike it's on, so it's a bike of its own again. */
    isNewBike: z.boolean().optional(),
  })

export type ServiceBike = z.infer<typeof ServiceBikeSchema>
export type UpdateServiceBike = z.infer<typeof UpdateServiceBikeSchema>

export interface ServiceBikeWithLastService extends ServiceBike {
  lastHandInDate: string
  /** Visits to the store, not counting the job being worked on. */
  visitCount: number
  /** The job being worked on already belongs to this bike. */
  isOnCurrentJob: boolean
}

const SERVICE_BIKE_TYPE_LABEL_KEYS: Record<ServiceBikeType, TranslationKey> = {
  [ServiceBikeType.ELECTRIC_SCOOTER]: "common.bikeType.electricScooter",
  [ServiceBikeType.MOPED]: "common.bikeType.moped",
  [ServiceBikeType.CITY]: "common.bikeType.city",
  [ServiceBikeType.HYBRID]: "common.bikeType.hybrid",
  [ServiceBikeType.MOUNTAIN]: "common.bikeType.mountain",
  [ServiceBikeType.GRAVEL]: "common.bikeType.gravel",
  [ServiceBikeType.COUNTRY_ROAD]: "common.bikeType.countryRoad",
  [ServiceBikeType.CARGO]: "common.bikeType.cargo",
  [ServiceBikeType.BALANCE_BIKE]: "common.bikeType.balanceBike",
  [ServiceBikeType.CHILDREN_BIKE]: "common.bikeType.childrenBike",
  [ServiceBikeType.JUNIOR_BIKE]: "common.bikeType.juniorBike",
  [ServiceBikeType.CYCLOCROSS]: "common.bikeType.cyclocross",
  [ServiceBikeType.VINTAGE]: "common.bikeType.vintage",
  [ServiceBikeType.FOLDABLE]: "common.bikeType.foldable",
  [ServiceBikeType.CRUISER]: "common.bikeType.cruiser",
  [ServiceBikeType.BMX]: "common.bikeType.bmx",
  [ServiceBikeType.FAT_BIKE]: "common.bikeType.fatBike",
  [ServiceBikeType.TRIKE]: "common.bikeType.trike",
  [ServiceBikeType.FIXED_GEAR]: "common.bikeType.fixedGear",
  [ServiceBikeType.LOOSE_PART]: "common.bikeType.loosePart",
  [ServiceBikeType.OTHER]: "common.bikeType.other",
  [ServiceBikeType.ALPINE_SKI]: "common.bikeType.alpineSki",
  [ServiceBikeType.CROSS_COUNTRY_SKI]: "common.bikeType.crossCountrySki",
  [ServiceBikeType.SNOWBOARD]: "common.bikeType.snowboard",
  [ServiceBikeType.TOURING_SKATES]: "common.bikeType.touringSkates",
  [ServiceBikeType.ICE_SKATES]: "common.bikeType.iceSkates",
}

export class ServiceBikeTypeMapper {
  static toLabel(
    type: ServiceBikeType,
    customBikeTypeName: string | null,
    t: Translator,
  ): string {
    if (customBikeTypeName !== null) {
      // Distinguishes user-defined types from the enum set.
      return t("common.bikeType.custom", { name: customBikeTypeName })
    }
    return t(ServiceBikeTypeMapper.toLabelKey(type))
  }

  static toLabelKey(type: ServiceBikeType): TranslationKey {
    return SERVICE_BIKE_TYPE_LABEL_KEYS[type]
  }

  /**
   * What one item of this type is called when counted, e.g. "Cykel 1" or
   * "Längdskidor 2". Every bicycle type is a "Cykel".
   */
  static toUnitLabelKey(type: ServiceBikeType): TranslationKey {
    if (BICYCLE_TYPES.includes(type) || type === ServiceBikeType.OTHER) {
      return "common.bikeType.unitBike"
    }
    return ServiceBikeTypeMapper.toLabelKey(type)
  }

  /**
   * Returns all bike types whose label contains the query string (case-insensitive).
   * Used for searching jobs by bike type label.
   */
  static findMatchingTypes(query: string, t: Translator): ServiceBikeType[] {
    const lowerQuery = query.toLowerCase()
    const allTypes = Object.values(ServiceBikeType) as ServiceBikeType[]
    return allTypes.filter((type) =>
      t(ServiceBikeTypeMapper.toLabelKey(type))
        .toLowerCase()
        .includes(lowerQuery),
    )
  }
}

export class ServiceBikeLabelUtils {
  /** "Crescent Elder", "Crescent" or "Elder"; null when both are empty. */
  static brandAndModel(
    brandName: string | null,
    model: string | null,
  ): string | null {
    const label = [brandName ? JuloUtils.capitalize(brandName) : null, model]
      .filter((part) => part !== null && part.trim() !== "")
      .join(" ")
    return label === "" ? null : label
  }
}

const GEAR_SYSTEM_LABEL_KEYS: Record<ServiceBikeGearSystem, TranslationKey> = {
  [ServiceBikeGearSystem.HUB]: "common.gearSystem.hub",
  [ServiceBikeGearSystem.EXTERNAL]: "common.gearSystem.external",
  [ServiceBikeGearSystem.SINGLE_SPEED]: "common.gearSystem.singleSpeed",
}

export class ServiceBikeGearSystemMapper {
  static toLabelKey(type: ServiceBikeGearSystem): TranslationKey {
    return GEAR_SYSTEM_LABEL_KEYS[type]
  }
}

const BRAKE_SYSTEM_LABEL_KEYS: Record<ServiceBikeBrakeSystem, TranslationKey> =
  {
    [ServiceBikeBrakeSystem.HYDRAULIC_DISC]: "common.brakeSystem.hydraulicDisc",
    [ServiceBikeBrakeSystem.MECHANIC_DISC]: "common.brakeSystem.mechanicDisc",
    [ServiceBikeBrakeSystem.HYDRAULIC_RIM]: "common.brakeSystem.hydraulicRim",
    [ServiceBikeBrakeSystem.MECHANIC_RIM]: "common.brakeSystem.mechanicRim",
    [ServiceBikeBrakeSystem.ROLLER]: "common.brakeSystem.roller",
    [ServiceBikeBrakeSystem.COASTER]: "common.brakeSystem.coaster",
    [ServiceBikeBrakeSystem.NONE]: "common.brakeSystem.none",
  }

export function brakeSystemLabelKey(
  type: ServiceBikeBrakeSystem,
): TranslationKey {
  return BRAKE_SYSTEM_LABEL_KEYS[type]
}

export interface BikeTypeDefaultConfig {
  brakeSystemFront: ServiceBikeBrakeSystem | undefined
  brakeSystemBack: ServiceBikeBrakeSystem | undefined
  gearSystem: ServiceBikeGearSystem | undefined
  bikeHasFrontGears: boolean
  bikeHasDamperFront: boolean
  bikeHasDamperBack: boolean
}

const NO_DEFAULTS: BikeTypeDefaultConfig = {
  brakeSystemFront: undefined,
  brakeSystemBack: undefined,
  gearSystem: undefined,
  bikeHasFrontGears: false,
  bikeHasDamperFront: false,
  bikeHasDamperBack: false,
}

type BikeTypeDefaultKey = ServiceBikeType | `${ServiceBikeType}_ELECTRIC`

const BIKE_TYPE_DEFAULTS: Partial<
  Record<BikeTypeDefaultKey, BikeTypeDefaultConfig>
> = {
  // Non-electric defaults
  [ServiceBikeType.CITY]: {
    brakeSystemFront: ServiceBikeBrakeSystem.MECHANIC_RIM,
    brakeSystemBack: ServiceBikeBrakeSystem.COASTER,
    gearSystem: ServiceBikeGearSystem.HUB,
    bikeHasFrontGears: false,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.HYBRID]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: true,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.MOUNTAIN]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: true,
    bikeHasDamperBack: true,
  },
  [ServiceBikeType.GRAVEL]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.COUNTRY_ROAD]: {
    brakeSystemFront: ServiceBikeBrakeSystem.MECHANIC_RIM,
    brakeSystemBack: ServiceBikeBrakeSystem.MECHANIC_RIM,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.CARGO]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.BALANCE_BIKE]: {
    brakeSystemFront: ServiceBikeBrakeSystem.NONE,
    brakeSystemBack: ServiceBikeBrakeSystem.NONE,
    gearSystem: undefined,
    bikeHasFrontGears: false,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.CHILDREN_BIKE]: {
    brakeSystemFront: ServiceBikeBrakeSystem.MECHANIC_RIM,
    brakeSystemBack: ServiceBikeBrakeSystem.MECHANIC_RIM,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: false,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.JUNIOR_BIKE]: {
    brakeSystemFront: ServiceBikeBrakeSystem.MECHANIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.MECHANIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.CYCLOCROSS]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.VINTAGE]: {
    brakeSystemFront: ServiceBikeBrakeSystem.MECHANIC_RIM,
    brakeSystemBack: ServiceBikeBrakeSystem.MECHANIC_RIM,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [ServiceBikeType.FOLDABLE]: {
    brakeSystemFront: ServiceBikeBrakeSystem.MECHANIC_RIM,
    brakeSystemBack: ServiceBikeBrakeSystem.MECHANIC_RIM,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },

  // Electric variants (keyed as TYPE_ELECTRIC)
  [`${ServiceBikeType.CITY}_ELECTRIC`]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.COASTER,
    gearSystem: ServiceBikeGearSystem.HUB,
    bikeHasFrontGears: false,
    bikeHasDamperFront: true,
    bikeHasDamperBack: false,
  },
  [`${ServiceBikeType.HYBRID}_ELECTRIC`]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: true,
    bikeHasDamperBack: false,
  },
  [`${ServiceBikeType.MOUNTAIN}_ELECTRIC`]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: true,
    bikeHasDamperBack: false,
  },
  [`${ServiceBikeType.GRAVEL}_ELECTRIC`]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [`${ServiceBikeType.COUNTRY_ROAD}_ELECTRIC`]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [`${ServiceBikeType.CARGO}_ELECTRIC`]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.EXTERNAL,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
  [`${ServiceBikeType.FOLDABLE}_ELECTRIC`]: {
    brakeSystemFront: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    brakeSystemBack: ServiceBikeBrakeSystem.HYDRAULIC_DISC,
    gearSystem: ServiceBikeGearSystem.HUB,
    bikeHasFrontGears: true,
    bikeHasDamperFront: false,
    bikeHasDamperBack: false,
  },
}

/**
 * Returns default bike configuration based on bike type and whether it's electric.
 * Used to pre-fill the inspection form when reception data is missing.
 */
export function getBikeTypeDefaults(
  type: ServiceBikeType,
  isElectric: boolean,
): BikeTypeDefaultConfig {
  if (isElectric) {
    const electricDefaults = BIKE_TYPE_DEFAULTS[`${type}_ELECTRIC`]
    if (electricDefaults) return electricDefaults
  }
  return BIKE_TYPE_DEFAULTS[type] ?? NO_DEFAULTS
}

export interface WheelCounts {
  frontWheelCount: number
  rearWheelCount: number
}

/** What reception suggests for a cargo bike: most have two wheels in front, around the box */
export const DEFAULT_CARGO_WHEEL_COUNTS: WheelCounts = {
  frontWheelCount: 2,
  rearWheelCount: 1,
}

const TRIKE_WHEEL_COUNTS: WheelCounts = {
  frontWheelCount: 1,
  rearWheelCount: 2,
}
const SINGLE_WHEEL_COUNTS: WheelCounts = {
  frontWheelCount: 1,
  rearWheelCount: 1,
}

/**
 * The wheels a bike actually has on each axle. A trike always has one in front
 * and two behind, and only a cargo bike's counts are picked by the mechanic.
 * Every other type has one wheel per axle, whatever is stored. A count that was
 * never picked (jobs received before it was asked) is one wheel.
 */
export const resolveWheelCounts = (
  type: ServiceBikeType,
  frontWheelCount: number | null | undefined,
  rearWheelCount: number | null | undefined,
): WheelCounts => {
  if (type === ServiceBikeType.TRIKE) return TRIKE_WHEEL_COUNTS
  if (type !== ServiceBikeType.CARGO) return SINGLE_WHEEL_COUNTS
  return {
    frontWheelCount: frontWheelCount ?? SINGLE_WHEEL_COUNTS.frontWheelCount,
    rearWheelCount: rearWheelCount ?? SINGLE_WHEEL_COUNTS.rearWheelCount,
  }
}
